/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/fields/MezzoSelectField.tsx
 * Release: v1.0.0
 * Descrizione: Campo selezione mezzi di un cliente
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';

interface Mezzo {
  id: string;
  tipo_mezzo: string;
  marca: string;
  modello: string;
  anno?: number;
  targa?: string;
  numero_telaio?: string;
  km_percorsi?: number;
  created_at?: string;
}

interface MezzoSelectFieldProps {
  value: string;
  onChange: (value: string, mezzoData?: Mezzo) => void;
  clienteId?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

export const MezzoSelectField: React.FC<MezzoSelectFieldProps> = ({
  value,
  onChange,
  clienteId,
  label = 'Mezzo',
  placeholder = 'Seleziona un mezzo registrato',
  required = false,
  disabled = false,
  error
}) => {
  const { getToken } = useAuth();
  const [mezzi, setMezzi] = useState<Mezzo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMezzo, setSelectedMezzo] = useState<Mezzo | null>(null);

  // Carica mezzi del cliente
  useEffect(() => {
    if (!clienteId) {
      setMezzi([]);
      setSelectedMezzo(null);
      return;
    }

    const loadMezzi = async () => {
      setIsLoading(true);
      try {
        const token = getToken();
        const response = await fetch(`/api/accettazione/mezzi/cliente/${clienteId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setMezzi(data.mezzi || []);
        } else {
          console.error('Errore caricamento mezzi:', await response.text());
          setMezzi([]);
        }
      } catch (err) {
        console.error('Errore caricamento mezzi:', err);
        setMezzi([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadMezzi();
  }, [clienteId, getToken]);

  // Gestione selezione mezzo
  const handleSelectMezzo = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const mezzoId = e.target.value;
    
    if (!mezzoId) {
      setSelectedMezzo(null);
      onChange('');
      return;
    }

    const mezzo = mezzi.find(m => m.id === mezzoId);
    if (mezzo) {
      setSelectedMezzo(mezzo);
      onChange(mezzoId, mezzo);
    }
  };

  // Reset quando clienteId cambia
  useEffect(() => {
    if (value && !mezzi.find(m => m.id === value)) {
      onChange('');
    }
  }, [mezzi, value, onChange]);

  // Icone per tipo mezzo
  const getMezzoIcon = (tipo: string) => {
    const icons: Record<string, string> = {
      auto: '🚗',
      camion: '🚚',
      trattore: '🚜',
      barca: '⛵',
      altro: '🔧'
    };
    return icons[tipo] || '🚗';
  };

  // Nome visualizzato del mezzo
  const getMezzoDisplayName = (mezzo: Mezzo): string => {
    const parts = [
      mezzo.marca,
      mezzo.modello,
      mezzo.anno ? `(${mezzo.anno})` : '',
      mezzo.targa ? `- ${mezzo.targa}` : ''
    ].filter(Boolean);
    
    return parts.join(' ');
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-slate-300">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          value={value}
          onChange={handleSelectMezzo}
          disabled={disabled || isLoading || !clienteId}
          required={required}
          className={`
            w-full px-3 py-2 bg-slate-700 border rounded-lg text-white 
            focus:border-blue-500 focus:ring-1 focus:ring-blue-500 
            transition-colors appearance-none
            ${error ? 'border-red-500' : 'border-slate-600'}
            ${disabled || isLoading || !clienteId ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <option value="">
            {!clienteId 
              ? 'Seleziona prima un cliente' 
              : isLoading 
                ? 'Caricamento mezzi...' 
                : mezzi.length === 0 
                  ? 'Nessun mezzo registrato'
                  : placeholder
            }
          </option>
          
          {mezzi.map((mezzo) => (
            <option key={mezzo.id} value={mezzo.id}>
              {getMezzoIcon(mezzo.tipo_mezzo)} {getMezzoDisplayName(mezzo)}
            </option>
          ))}
        </select>

        {/* Icona dropdown */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <svg className="animate-spin h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>

      {/* Messaggio errore */}
      {error && (
        <div className="text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Dettagli mezzo selezionato */}
      {selectedMezzo && (
        <div className="mt-2 p-3 bg-slate-700/30 rounded-lg border border-slate-600">
          <div className="text-sm text-slate-400 mb-2">Dettagli mezzo:</div>
          
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-slate-400">Tipo:</span>
              <div className="text-white">
                {getMezzoIcon(selectedMezzo.tipo_mezzo)} {selectedMezzo.tipo_mezzo}
              </div>
            </div>
            
            {selectedMezzo.targa && (
              <div>
                <span className="text-slate-400">Targa:</span>
                <div className="text-white font-mono">{selectedMezzo.targa}</div>
              </div>
            )}
            
            {selectedMezzo.numero_telaio && (
              <div>
                <span className="text-slate-400">Telaio:</span>
                <div className="text-white font-mono text-xs">{selectedMezzo.numero_telaio}</div>
              </div>
            )}
            
            {selectedMezzo.km_percorsi !== undefined && selectedMezzo.km_percorsi !== null && (
              <div>
                <span className="text-slate-400">Km:</span>
                <div className="text-white">{selectedMezzo.km_percorsi.toLocaleString('it-IT')}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info conteggio mezzi */}
      {clienteId && mezzi.length > 0 && (
        <div className="text-xs text-slate-400">
          {mezzi.length} mezzo{mezzi.length !== 1 ? 'i' : ''} disponibile{mezzi.length !== 1 ? 'i' : ''}
        </div>
      )}
    </div>
  );
};